import { startCase } from "lodash/fp";
import { DateTime, Duration } from "luxon";

const UNIT_ABBREVIATIONS = {
  seconds: { self: "s", parent: "m" },
  minutes: { self: "m", parent: "h" },
};

interface GetFormattedDurationOpts {
  unit: "seconds" | "minutes";
  /** If true, will exclude remainder from rounded parent value (e.g. 66 seconds -> "1m" instead of "1m 6s) */
  round?: boolean;
}

interface GetFormattedDuration {
  (duration: number, opts: GetFormattedDurationOpts): string | null;
}

export const getFormattedDuration: GetFormattedDuration = (
  duration,
  { unit, round = false }
) => {
  if (!duration) return null;

  const { self: selfDurationLabel, parent: parentDurationLabel } =
    UNIT_ABBREVIATIONS[unit];

  if (duration > 60) {
    const minutesOrHours = Math.floor(duration / 60);
    const remainingSecondsOrMinutes = duration % 60;
    if (remainingSecondsOrMinutes > 0 && !round) {
      return `${minutesOrHours}${parentDurationLabel} ${Math.round(
        remainingSecondsOrMinutes
      )}${selfDurationLabel}`;
    }
    return `${minutesOrHours}${parentDurationLabel}`;
  }

  return `${duration < 1 ? "<1" : Math.round(duration)}${selfDurationLabel}`;
};

export const getFormattedRemainingDuration: GetFormattedDuration = (
  duration,
  opts
) => {
  const formattedDuration = getFormattedDuration(duration, opts);
  return formattedDuration ? `${formattedDuration} left` : null;
};

export const formatVideoTimeFromSeconds = (time?: number | null) => {
  if (time == null) return "--:--";

  if (time < 3600) {
    return Duration.fromMillis(time * 1000).toFormat("m:ss");
  }
  return Duration.fromMillis(time * 1000).toFormat("h:mm:ss");
};

export const getRoundedValues = (rawValue: number | null, suffix = "") => {
  if (rawValue == null) {
    return { value: null, formattedValue: "-", rawValue };
  }

  if (rawValue > 0 && rawValue < 1) {
    return { value: rawValue, formattedValue: `<1${suffix}`, rawValue };
  }

  const roundedValue = Math.round(rawValue);
  return {
    value: roundedValue,
    formattedValue: `${roundedValue}${suffix}`,
    rawValue,
  };
};

/**
 * @param date date for which we want the relative date. by default, compares to "now"
 * @param relativeTo? primarily for testing. if passed, will compare the date param to this date instead of "now"
 * */
export const getRelativeDisplayDate = (
  date: DateTime,
  relativeTo?: DateTime
) => {
  const relativeDate =
    date.toRelativeCalendar(relativeTo ? { base: relativeTo } : undefined) ??
    "";
  return relativeDate.match(/^\d/) ? relativeDate : startCase(relativeDate);
};
